import type { StreamEvent, Block, ConnectionStatus, AgentStreamConfig } from './types';
import { SSEFetcher } from './SSEFetcher';
import { BlockStore } from './BlockStore';
import { Reconnector } from './Reconnector';

export class AgentStreamClient {
  private fetcher: SSEFetcher;
  private blockStore: BlockStore;
  private reconnector: Reconnector;
  private config: AgentStreamConfig;

  private status: ConnectionStatus = 'disconnected';
  private isRunning: boolean = false;
  private error: string | null = null;

  constructor(config: AgentStreamConfig) {
    this.config = config;
    this.fetcher = new SSEFetcher();
    this.blockStore = new BlockStore();
    this.reconnector = new Reconnector(
      config.maxReconnectAttempts,
      config.reconnectDelay,
      config.maxReconnectDelay
    );
  }

  /**
   * Update configuration (useful for updating headers without reconnecting)
   */
  updateConfig(config: Partial<AgentStreamConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Connect to the SSE stream
   */
  async connect(): Promise<void> {
    // Don't reconnect if already connected
    if (this.status === 'connected' || this.status === 'connecting') {
      return;
    }

    this.setStatus('connecting');
    this.error = null;

    try {
      await this.fetcher.connect(
        this.config.url,
        {
          method: this.config.method || 'GET',
          headers: this.config.headers || {},
          body: this.config.body,
        },
        this.handleEvent.bind(this),
        this.handleError.bind(this)
      );

      this.setStatus('connected');
    } catch (err) {
      const error = err as Error;
      this.error = error.message;
      this.setStatus('error');
      throw err;
    }
  }

  /**
   * Disconnect from the stream
   */
  disconnect(): void {
    this.fetcher.disconnect();
    this.reconnector.cancel();
    this.setStatus('disconnected');
  }

  /**
   * Reset the client state
   */
  reset(): void {
    this.blockStore.reset();
    this.reconnector.reset();
    this.isRunning = false;
    this.error = null;
  }

  /**
   * Get all blocks (returns immutable copy)
   */
  getBlocks(): Map<string, Block> {
    return this.blockStore.getBlocks();
  }

  /**
   * Get current connection status
   */
  getStatus(): ConnectionStatus {
    return this.status;
  }

  /**
   * Check if agent is currently running
   */
  isAgentRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Get current error message
   */
  getError(): string | null {
    return this.error;
  }

  /**
   * Handle incoming stream events
   */
  private handleEvent(event: StreamEvent): void {
    // Handle control events (backend sends "start" and "finish")
    if (event.type === 'start') {
      this.isRunning = true;
      this.config.onAgentRunningChange?.(true);
      return;
    }

    if (event.type === 'finish') {
      this.isRunning = false;
      this.config.onAgentRunningChange?.(false);
      return;
    }

    // Route event to block store
    const updatedBlock = this.blockStore.handleEvent(event);

    // Notify callback if block was updated
    if (updatedBlock && this.config.onBlockUpdate) {
      this.config.onBlockUpdate(updatedBlock);
    }
  }

  /**
   * Handle stream errors
   */
  private handleError(error: Error): void {
    this.config.onError?.(error);
  }

  /**
   * Set connection status and notify callback
   */
  private setStatus(status: ConnectionStatus): void {
    this.status = status;
    this.config.onStatusChange?.(status);
  }
}
